"use client";

// The dark/light switch that sits at the top of the user menu.
//
// A real switch, not a menu item: the thing being changed is a two-position
// setting, and a switch is the control that says so — it shows the current
// value at rest, which a "Switch to light mode" button cannot.
//
// Built on a plain <button role="switch">, which is the accessible primitive for
// exactly this and needs no library. aria-checked carries the state, so a screen
// reader announces "Dark mode, switch, on" and its own toggle gesture works.

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";
import clsx from "clsx";

export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();

  // MOUNT GATE — the one piece of ceremony next-themes requires, and skipping it
  // is the classic bug with it. The server has no idea what this browser stored,
  // so the HTML it sends cannot contain the right thumb position or the right
  // icon. Rendering the real state on the first client pass would therefore
  // hydrate a different tree than the server sent. So the first pass renders a
  // fixed placeholder that MATCHES the server, and the true state lands in the
  // pass right after mount. The page itself does not flash — the class on <html>
  // was already set by next-themes' blocking script — only this one control
  // resolves a beat later, and it holds its own size while it does.
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- the mount gate above: the stored theme is unknowable on the server
  useEffect(() => setMounted(true), []);

  const isDark = !mounted || resolvedTheme === "dark";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label="Dark mode"
      data-testid="theme-toggle"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-card"
    >
      <span className="flex items-center gap-2 text-[12px] font-medium text-text">
        {isDark ? <Moon size={13} className="text-accent" /> : <Sun size={13} className="text-accent" />}
        {isDark ? "Dark mode" : "Light mode"}
      </span>

      {/* The track. aria-hidden because the <button> above already carries the
          whole meaning — the switch is a picture of the state, not a second
          control to tab to. */}
      <span
        aria-hidden
        className={clsx(
          "relative shrink-0 h-[18px] w-[32px] rounded-full transition-colors",
          isDark ? "bg-accent/40" : "bg-line",
        )}
      >
        <span
          className={clsx(
            "absolute top-[2px] h-[14px] w-[14px] rounded-full transition-all duration-150",
            isDark ? "left-[16px] bg-accent" : "left-[2px] bg-surface shadow-sm",
          )}
        />
      </span>
    </button>
  );
}
